import { Component, Input, inject } from '@angular/core';
import { SmartCartPreviewStateService } from '../../smart-cart-preview-state.service';
import {
  CART_ITEM_IMAGE_DEFAULTS,
  CartItemsSettings,
  GlobalButtonDesign,
  GLOBAL_BUTTON_DESIGN_DEFAULT,
} from '../widget-preview.types';

const SAMPLE_ITEM = {
  image: 'assets/icons/checkout_content_default.png',
  title: 'Sample product',
  priceCents: 2499,
  originalPriceCents: 2999,
  quantity: 1,
  variantOptions: [
    { name: 'Color', values: ['Black', 'White', 'Blue'], selected: 'Black' },
    { name: 'Size', values: ['S', 'M', 'L'], selected: 'M' },
  ],
  samplePlanDiscount: 15,
};

interface ResolvedSubscriptionStyles {
  backgroundColor: string;
  textColor: string;
  hoverColor: string;
  iconColor: string;
  cornerRadius: number;
}

@Component({
  selector: 'cart-items-preview',
  templateUrl: './cart-items-preview.component.html',
  styleUrls: ['./cart-items-preview.component.scss'],
  standalone: false,
})
export class CartItemsPreviewComponent {
  @Input() settings!: CartItemsSettings;
  @Input() globalButtonDesign: GlobalButtonDesign = GLOBAL_BUTTON_DESIGN_DEFAULT;

  readonly sample = SAMPLE_ITEM;
  readonly showEmptyCart = inject(SmartCartPreviewStateService).showEmptyCart;

  // Mirrors the storefront's CartItem box so the preview shows the merchant's chosen size, not a fixed one.
  // Absent settings fall back to the same defaults the storefront uses. Fit isn't previewed: the sample
  // asset is a placeholder glyph, and cropping an icon would show the merchant nothing useful.
  get imageWidth(): number {
    return this.settings?.image?.width ?? CART_ITEM_IMAGE_DEFAULTS.width;
  }

  get imageHeight(): number {
    return this.settings?.image?.height ?? CART_ITEM_IMAGE_DEFAULTS.height;
  }

  get imageBackground(): string {
    return this.settings?.image?.backgroundColor || CART_ITEM_IMAGE_DEFAULTS.backgroundColor;
  }

  // Mirrors the storefront: 'bottom' pins the quantity row to the base of the line, 'inline' packs it
  // directly under the details.
  get contentJustify(): string {
    return this.settings?.quantityInput?.position === 'inline' ? 'flex-start' : 'space-between';
  }

  get formattedPrice(): string {
    return this.formatMoney(this.sample.priceCents);
  }

  get formattedOriginalPrice(): string {
    return this.formatMoney(this.sample.originalPriceCents);
  }

  get hasDiscount(): boolean {
    return this.sample.originalPriceCents > this.sample.priceCents;
  }

  get qtyRange(): number[] {
    const { min, max } = this.settings.quantityInput;
    const lo = Math.max(1, min);
    const hi = Math.max(lo, max);
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  }

  // Subscription `design.hoverColor` resolves from global's `hoverTextColor` (storefront convention).
  get resolvedSubscription(): ResolvedSubscriptionStyles {
    const d = this.settings.subscription.design;
    const g = this.globalButtonDesign;
    return {
      backgroundColor: d.backgroundColor ?? g.backgroundColor,
      textColor: d.textColor ?? g.textColor,
      hoverColor: d.hoverColor ?? g.hoverTextColor,
      iconColor: d.iconColor ?? g.iconColor,
      cornerRadius: d.cornerRadius ?? g.cornerRadius,
    };
  }

  get subscriptionLabel(): string {
    return this.settings.subscription.label
      .replace(/\{subscription_discount\}%/g, `${this.sample.samplePlanDiscount}%`)
      .replace(/\{subscription_discount\}/g, String(this.sample.samplePlanDiscount));
  }

  onSubHoverEnter(event: MouseEvent): void {
    (event.currentTarget as HTMLElement).style.backgroundColor = this.resolvedSubscription.hoverColor;
  }

  onSubHoverLeave(event: MouseEvent): void {
    (event.currentTarget as HTMLElement).style.backgroundColor = this.resolvedSubscription.backgroundColor;
  }

  private formatMoney(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }
}
